import type { FC, FormEvent } from 'react';
import { memo, useState } from 'react';
import { BiEnvelope, BiPhone } from 'react-icons/bi';

import useGaTracker from '../components/PageTracking';
import useSeo from '../components/useSeo';

const Kontakt: FC = memo(() => {
  const [formStatus, setFormStatus] = useState('');

  useGaTracker();
  useSeo({
    title: 'Kontakt | Toft Ejendomme',
    description:
      'Kontakt Toft Ejendomme i Silkeborg. Find kontaktoplysninger til udlejer og vicevært eller send os en besked.',
    path: '/kontakt',
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const name = String(formData.get('name') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const subject = String(formData.get('subject') || '').trim();
    const message = String(formData.get('message') || '').trim();

    if (!name || !email || !message) {
      setFormStatus('Udfyld venligst navn, e-mail og besked.');
      return;
    }

    const fullSubject = subject || 'Henvendelse fra hjemmesiden';
    const body = `Navn: ${name}\nE-mail: ${email}\n\n${message}`;

    window.location.href = `mailto:info@toftejendomme.dk?subject=${encodeURIComponent(
      fullSubject
    )}&body=${encodeURIComponent(body)}`;
    setFormStatus('Din mailklient åbnes nu med beskeden udfyldt.');
    event.currentTarget.reset();
  };

  return (
    <>
      <section className="page-masthead page-masthead--plain page-flow">
        <div className="section-wrap">
          <div className="contact-layout">
            <div className="contact-copy-panel">
              <span className="eyebrow">Kontaktoplysninger</span>
              <h2 className="section-title">Vi hjælper gerne</h2>
              <p className="contact-copy">
                Som mindre, lokalt forankret udlejer er det vigtigt for os, at det er let at få fat i os.
                Skriv til os, hvis du vil høre om en bolig, ventelisten eller praktiske forhold.
              </p>

              <div className="contact-list">
                <div className="contact-item">
                  <span className="contact-icon">
                    <BiEnvelope />
                  </span>
                  <div>
                    <div className="contact-label">E-mail</div>
                    <div className="contact-value">
                      <a href="mailto:info@toftejendomme.dk">info@toftejendomme.dk</a>
                    </div>
                  </div>
                </div>

                <div className="contact-item">
                  <span className="contact-icon">
                    <BiPhone />
                  </span>
                  <div>
                    <div className="contact-label">Udlejer</div>
                    <div className="contact-value">Torben Toft</div>
                    <div className="contact-value">40 30 34 35</div>
                    <div className="contact-value">Mandag-fredag 9:00-12:00</div>
                  </div>
                </div>

                <div className="contact-item">
                  <span className="contact-icon">
                    <BiPhone />
                  </span>
                  <div>
                    <div className="contact-label">Vicevært</div>
                    <div className="contact-value">Janus Skov Nielsen</div>
                    <div className="contact-value">30 45 01 64</div>
                    <div className="contact-value">Mandag-fredag 9:00-10:00</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="site-surface" style={{ padding: 'clamp(1.5rem, 3vw, 2rem)' }}>
              <span className="eyebrow">Skriv til os</span>
              <h2 className="section-title" style={{ marginBottom: '1.2rem' }}>
                Send en besked
              </h2>

              <form className="form-grid" onSubmit={handleSubmit}>
                <div className="form-field">
                  <label htmlFor="contact-name">Navn</label>
                  <input id="contact-name" name="name" placeholder="Dit fulde navn" type="text" />
                </div>

                <div className="form-field">
                  <label htmlFor="contact-email">E-mail</label>
                  <input
                    id="contact-email"
                    name="email"
                    placeholder="navn@eksempel.dk"
                    type="email"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="contact-subject">Emne</label>
                  <select id="contact-subject" name="subject">
                    <option value="">Vælg emne</option>
                    <option value="Spørgsmål om ledige lejemål">Spørgsmål om ledige lejemål</option>
                    <option value="Spørgsmål om eksisterende lejemål">Spørgsmål om eksisterende lejemål</option>
                    <option value="Venteliste">Venteliste</option>
                    <option value="Andet">Andet</option>
                  </select>
                </div>

                <div className="form-field">
                  <label htmlFor="contact-message">Besked</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    placeholder="Skriv din besked her…"
                    rows={6}
                  />
                </div>

                {formStatus ? (
                  <div className="form-success is-visible" role="status">
                    {formStatus}
                  </div>
                ) : null}

                <button className="site-btn site-btn--primary" type="submit">
                  Send besked →
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
});

Kontakt.displayName = 'Kontakt';

export default Kontakt;
